import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { DbService } from '@services/db.service';
import { ReportService } from '@services/report.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, Subscription } from 'rxjs';
import { JsonViewerComponent } from 'src/app/components/modals/json-viewer/json-viewer.component';
import { User } from 'src/app/models/User';
import { WorkflowReport } from 'src/app/models/WorkflowReport';
import { DetailPageService } from 'src/app/services/detail-page.service';
import { LoaderService } from 'src/app/services/support/loader.service';
import { UtilityService } from '@services/remote/utility.service';
import { WorkflowService } from 'src/app/services/workflow.service';
import { environment } from 'src/environments/environment';
import { ReportComponent } from '../report/report.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertModalComponent } from 'src/app/components/modals/alert-modal/alert-modal.component';
import { AuthService } from '@services/auth.service';
import { SessionListComponent } from '../session-list/session-list.component';

@Component({
    selector: 'app-report-list',
    templateUrl: './report-list.component.html',
    styleUrls: ['./report-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportListComponent implements OnInit {
  @ViewChild(MatSort, { static: false })
  set sort(sorter: MatSort) {
    this.dataSource.sort = sorter;
  }

  @ViewChild(MatPaginator, { static: false })
  set paginator(paginator: MatPaginator) {
    if (paginator) {
      this._paginator = paginator;
      this.dataSource.paginator = paginator;
    }
  }
  private _paginator: MatPaginator;

  @ViewChild(SessionListComponent, { static: false })
  sessionListComponent: SessionListComponent;

  dataSource = new MatTableDataSource<any>()
  displayedColumns: string[] = [
    "name",
    "createdAt",
    "version",
    "user",
    "status",
    "select"
  ];

  reportList: WorkflowReport[] = []
  isLoading = false

  allUsers: User[] = []
  usersLoading: boolean = true
  subscriptions: Subscription[] = []
  reportUser = ''
  reportSelected
  loadedReport;
  workflowPublishedVersion
  newArray = []
  isDownloading

  isLoadingMore = false;
  limit = 10;

  isAdmin: boolean = false
  hasEditorAccess: boolean = false

  private originalReportList = [];

  showCountPanel = false
  licenseCount
  licenseLimit
  isEwers = false

  filterForm: UntypedFormGroup = new UntypedFormGroup({
    nameFilter: new UntypedFormControl(),
    labelFilter: new UntypedFormControl()
  })

  filterStartEnd: Date[] = []
  filterUsers: string[] = []

  tabIndex = 0
  sessionListLoaded = false

  noRemoteLicense = true
  noWorkflowLicense = true

  constructor(
    private authService: AuthService,
    private workflowService: WorkflowService,
    private detailPageService: DetailPageService,
    private route: ActivatedRoute,          
    private loaderService:LoaderService,
    private snackBarService: SnackbarService,
    private reportService: ReportService,
    private modalService: BsModalService,
    private dbService: DbService,
    private translateService: TranslateService,
    private utilityService: UtilityService,
    private router: Router
  ) {
    this.subscriptions.push(this.authService.user.subscribe(user => {
      this.isAdmin = user.role === 'admin' || user.role === 'coadmin' || user.coadmin
      this.hasEditorAccess = user.editorAccess
    }))
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['tab'] === "remote") {
        this.tabIndex = 1
        this.sessionListLoaded = true
      } else {
        this.tabIndex = 0
        if (!this.reportList.length) {
          this.loaderService.show();
        }
      }
    });

    this.subscriptions.push(this.route.queryParams.subscribe(res => {
      if (res['report'] && !res['step'] && !this.loadedReport) {
        this.getSingleReport(res['report']).then((report) => this.openReport(report));
      }
      if (res['step'] && !res['index'] && !this.loadedReport) {
        this.getSingleReport(res['report']).then((report) => this.openReport(report, res['step'], res['key']));
      }
      if (res['index'] && !this.loadedReport) {
        this.getSingleReport(res['report']).then((report) => this.openReport(report, res['step'], res['key'], res['index']));
      }

    }))

    this.isEwers = (environment.design.shortName == 'ewers')

    this.reportListAssign()
    // this.getReportList();
    this.subscriptions.push(this.filterForm.valueChanges.pipe(debounceTime(300)).subscribe(res => {
      this.filterReports(res);
    }))

    this.checkLicense();
  }

  private checkLicense() {
    Promise.all([
      this.authService.getAccountData(),
      this.utilityService.getServerTimeOffset()
    ]).then(([account, offset]) => {
      const currentTime = (new Date()).getTime()

      const license = account.data.accountData.license
      const now = currentTime + offset
      const twelveHoursInMs = 1000*60*60*12

      this.noRemoteLicense = !license.remote_account || (license.remote_start - twelveHoursInMs > now) || (license.remote_end + twelveHoursInMs < now)
      this.noWorkflowLicense = !license.workflow_account || (license.workflow_start - twelveHoursInMs > now) || (license.workflow_end + twelveHoursInMs < now)
    });
  }

  onAllReports() {
    this.tabIndex = 0
    this.router.navigate(['/reports/workflow']);
  }

  onAllSessions() {
    this.tabIndex = 1
    this.router.navigate(['/reports/remote']);
  }

  showJsonSource(event, report) {
    event.stopPropagation()

    this.modalService.show(JsonViewerComponent, {
      initialState: {
        title: report.workflowName,
        json: report,
        expanded: false
      },
      backdrop: 'static',
      class: 'modal-lg modal-dialog-centered',
      animated: false
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  private setupSorting(dataSource: MatTableDataSource<any>) {
    dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case "name":
          return item.workflowName;
        case "createdAt":
          return item.createdAt;
        case "user":
          // Handle guest users
          if (item.guest?.email) {
            return `${item.guest.email} (Guest)`;
          }
          // Handle regular users
          if (item.userId) {
            return this.getUsersName(item.userId);
          }
          // Handle unknown users
          return "Unknown User";
        default:
          if (item.createdAt) {
            return item.createdAt;
          }
      }
    };
    dataSource.sort = this.sort;
    if (this._paginator) {
      dataSource.paginator = this._paginator;
    }
  }

  getUsersName(uid) {
    // First try to find in the current allUsers (which might be filtered)
    let user = this.allUsers.find(u => u.id === uid);
    
    // If not found and we're dealing with a guest
    if (!user && uid && uid.startsWith('guest_')) {
      const email = uid.replace('guest_', '');
      return `${email} (Guest)`;
    }
    
    if (user) {
      return user.name;
    } else {
      // Return placeholder if user not found
      return uid ? `User ${uid}` : "";
    }
  }

  getSingleReport(reportId) {
    //this.loaderService.show()
    return this.workflowService.getAllWorkflowReports(true).then(reports => reports.reportList.find((report) => reportId == report.id))
      .catch(err => this.snackBarService.error(this.translateService.instant('MAIN.CONTENT.REPORT-LIST.ERROR')))
      .finally(() => this.loaderService.hide())
  }

  getReportList() {
    this.isLoading = true
    this.initializeReports().then(() => {
      this.setupSorting(this.dataSource);
      this.isLoading = false
      this.loaderService.hide()
    })
  }

  async reportListAssign() {
    this.isLoading = true
    
    // Load users and reports in parallel
    const usersPromise = !environment.electron
      ? this.workflowService.getAllUsersForWorkflow().catch(error => {
          console.error('Error loading users:', error);
          return { users: [] };
        })
      : Promise.resolve({ users: [] });

    const reportsPromise = this.workflowService.getAllWorkflowReports(true);

    const [usersResult, res] = await Promise.all([usersPromise, reportsPromise]);

    this.allUsers = usersResult.users.filter(u => !u.deleted);

    if(res.license?.exceeded) {
      this.licenseCount = res.license.count
      this.licenseLimit = res.license.limit
      this.showCountPanel = true
    }
    this.reportList = res.reportList
    this.dataSource = new MatTableDataSource(this.reportList)
    this.mapReportList()
  }

  async mapReportList() {
    this.reportList.sort(function(a, b) { 
      if (a.createdAt < b.createdAt) {return 1}
      else if (a.createdAt > b.createdAt){return -1}
      return 0;
    })
    
    // Create user list from submitted reports
    this.createUserListFromReports();
    
    // Set default date filter to last week and apply it
    this.setDefaultDateFilter();
    this.filterReports(this.filterForm.value);
    
    this.isLoading = false
    this.setupSorting(this.dataSource);

    this.loaderService.hide()
  }

  createUserListFromReports() {
    const uniqueUsers = new Map();
    const reportUserIds = new Set();
    
    // First pass: collect all user IDs and guest emails from reports
    this.reportList.forEach((report) => {
      // Guest users - prioritize guest email if it exists
      if (report.guest?.email) {
        const guestId = `guest_${report.guest.email}`;
        uniqueUsers.set(guestId, {
          id: guestId,
          name: `${report.guest.email} (Guest)`,
          email: report.guest.email,
          isGuest: true
        });
      } else if (report.userId) {
        // Regular users - only if no guest email
        reportUserIds.add(report.userId);
      }
    });
    
    // Second pass: add regular users who have submitted reports (and are not guests)
    reportUserIds.forEach(userId => {
      const user = this.allUsers.find(u => u.id === userId);
      if (user) {
        uniqueUsers.set(userId, {
          id: userId,
          name: user.name,
          isGuest: false
        });
      } else {
        // If user not found in allUsers, create a placeholder
        uniqueUsers.set(userId, {
          id: userId,
          name: `Unknown User (${userId})`,
          isGuest: false
        });
      }
    });
    
    // Convert to array and sort alphabetically
    this.allUsers = Array.from(uniqueUsers.values())
      .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
  }

  async initializeReports(): Promise<void> {
    this.originalReportList = await this.workflowService.getAllWorkflowReports(true);
    this.originalReportList = this.originalReportList.sort(function (a, b) {
      const firstDate = new Date(a.createdAt);
      const secondDate = new Date(b.createdAt);

      if (firstDate < secondDate) { return 1 }
      else if (firstDate > secondDate) { return -1 }
      return 0;
    });
    this.reportList = this.originalReportList
    this.dataSource = new MatTableDataSource(this.reportList);
  }

  async export() {
    try {
      // Remote Sessions tab
      if (this.tabIndex === 1) {
        if (this.sessionListComponent) {
          this.sessionListComponent.onSessionExport();
        } else {
          this.snackBarService.error('Session list not loaded');
        }
        return;
      }
      
      // Workflow Reports tab
      this.isDownloading = true
      
      // Check if there's data to export
      if (!this.dataSource.filteredData || this.dataSource.filteredData.length === 0) {
        this.snackBarService.error(this.translateService.instant('MAIN.CONTENT.REPORT-LIST.NO-DATA-TO-EXPORT') || 'No data to export');
        this.isDownloading = false;
        return;
      }
      
      let workflowIds = []
      let distinctIds: any
      let index = 0;
      for (const wfReportData of this.dataSource.filteredData) {
        let cloneReportData = JSON.parse(JSON.stringify(wfReportData))
        index++;
        workflowIds.push(cloneReportData.workflowId)
        distinctIds = new Set([...workflowIds])

      }
      await this.mapListForCsv(distinctIds)
    } catch (error) {
      console.error('Error exporting reports:', error);
      this.snackBarService.error(this.translateService.instant('MAIN.CONTENT.REPORT-LIST.EXPORT-ERROR') || 'Error exporting reports');
      this.isDownloading = false;
    }
  }

  async mapListForCsv(distinctIds?, id?) {
    try {
      let workflows = []
      let workflowObject = {}
      let exportArray = []

      for await (const results of distinctIds) {
        await this.workflowService.getAllPublishedWorkflowVersions(results).then(workflow => {
          workflowObject[results] = workflow
          workflows.push(workflow)
        }).catch(err => {
          console.error('Error fetching workflow versions:', err);
          throw err;
        })
      }

    for (const wfReportData of this.dataSource.filteredData) {
      const wf = workflowObject[wfReportData.workflowId].find(res => res.version == wfReportData.version)

      let cloneReportData = JSON.parse(JSON.stringify(wfReportData))

      cloneReportData.workflowDescription = wf.translations ? wf.translations[wf.defaultLanguage][wf.description] : cloneReportData.workflowDescription

      if (cloneReportData.results && cloneReportData.results.length) {
        cloneReportData.results.forEach((reportData) => {
          try {
            if (!Object.keys(reportData.data).length) {
              reportData.data = { isValid: true, labels: [] };
            }
            this.loaderService.hide();
            let duration = 0;
            reportData.durations?.forEach((durationData) => {
              duration += durationData?.duration;
            });

            if (wf) {
              const stepData = wf?.steps?.[reportData.stepId]?.data;
              if (stepData) {
                let cloneStepData = JSON.parse(JSON.stringify(stepData));

                if (wf.translations) {
                  cloneStepData.name = wf.defaultLanguage
                    ? wf.translations[wf.defaultLanguage][cloneStepData.name]
                    : wf.translations['en'][cloneStepData.name];
                  cloneStepData.layouts.forEach((layout) => {
                    layout.items[0].templateOptions.label = wf.defaultLanguage
                      ? wf.translations[wf.defaultLanguage][
                          layout.items[0].templateOptions.label
                        ]
                      : wf.translations['en'][
                          layout.items[0].templateOptions.label
                        ];
                  });
                }
                let firstLayout = cloneStepData.layouts[0]?.items[0].key;
                let secondLayout = cloneStepData.layouts[1]?.items[0].key;
                let reportArray = [];
                reportArray.push({
                  key: firstLayout,
                  data: reportData.data[firstLayout],
                  index: 0,
                });
                if (secondLayout) {
                  reportArray.push({
                    key: secondLayout,
                    data: reportData.data[secondLayout],
                    index: 1,
                  });
                }
                reportData['reportArray'] = reportArray;
                reportData['stepName'] = cloneStepData.name;
                reportData['duration'] =
                  duration && duration > 0
                    ? '~' + this.convertMsToMinutesAndSecons(duration)
                    : 'Less than 1s';

                reportData = this.reportService.mappingReport(
                  reportData,
                  cloneStepData,
                  wf
                );
                if (wf.translations) {
                  reportData.reportArray.forEach((report) => {
                    if (report.data.type == 'radio') {
                      let radioVal;
                      if (Array.isArray(report.data.value)) {
                        radioVal = report.data.options.find(
                          (option) => option.value == report.data.value[0]
                        );
                      } else {
                        radioVal = report.data.options.find(
                          (option) => option.label == report.data.value
                        );
                      }
                      if (radioVal) {
                        report.data.value =
                          wf.translations[wf.defaultLanguage][radioVal.label];
                      }
                    }
                    if (report.data.type == 'multicheckbox') {
                      let multiVal = [];
                      const values = report.data.values ?? [];
                      values.forEach((res) => {
                        let val = report.data.options.filter(
                          (option) => option.label == res
                        );
                        if (val.length) {
                          multiVal.push(
                            wf.translations[wf.defaultLanguage][res]
                          );
                        }
                      });
                      report.data.value = multiVal;
                    }
                  });
                }
              }
            }
          } catch (err) {
            console.error('Report list processing error:', err);
            console.error('reportData:', reportData);
            console.error('stepId:', reportData?.stepId);
            throw err;
          }
        });
      }
      exportArray = exportArray.concat(this.reportService.mapReportDataForCsvExport(cloneReportData, wf, this.getUsersName(wfReportData.userId), wfReportData.id))
    }
    
    if (exportArray.length === 0) {
      throw new Error('No data to export');
    }
    
    this.reportService.download(exportArray)
    this.isDownloading = false
    } catch (error) {
      console.error('Error in mapListForCsv:', error);
      this.isDownloading = false;
      throw error;
    }
  }

  convertMsToMinutesAndSecons(ms) {
    let minutes = (ms / 1000) / 60;
    let seconds = (ms / 1000) % 60;
    if (minutes >= 1) {
      return (Math.floor(minutes) + " m " + Math.ceil(seconds) + " s")
    } else {
      return Math.ceil(seconds) + "s"
    }
  }

  filterReports(filteredForm) {
    let filteredData = this.reportList.filter((report) => {
      // Name filter
      const nameMatch = !filteredForm.nameFilter || (report.workflowName ? report.workflowName.toLocaleLowerCase().includes(filteredForm.nameFilter.toLocaleLowerCase()) : false);
      
      // Label filter
      const labelMatch = !filteredForm.labelFilter || (report['filterValue'] ? report['filterValue'].toLocaleLowerCase().includes(filteredForm.labelFilter.toLocaleLowerCase()) : false);
      
      // User filter
      let userMatch = true;
      if (this.filterUsers?.length > 0) {
        const regularUserMatch = report.userId && this.filterUsers.includes(report.userId);
        const guestUserMatch = report.guest?.email && this.filterUsers.includes(`guest_${report.guest.email}`);
        userMatch = regularUserMatch || guestUserMatch;
      }
      
      // Date range filter
      let dateMatch = true;
      if (this.filterStartEnd?.length === 2 && this.filterStartEnd[0] && this.filterStartEnd[1]) {
        const reportDate = new Date(report.createdAt);
        const startDate = new Date(this.filterStartEnd[0]);
        const endDate = new Date(this.filterStartEnd[1]);
        
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        dateMatch = reportDate >= startDate && reportDate <= endDate;
      }
      
      return nameMatch && labelMatch && userMatch && dateMatch;
    });
    
    this.dataSource = new MatTableDataSource(filteredData);
    this.setupSorting(this.dataSource);
  }

  onDateRangeChange() {
    this.filterReports(this.filterForm.value);
  }

  onUserFilterChange() {
    this.filterReports(this.filterForm.value);
  }

  clearDateFilters() {
    this.filterStartEnd = [];
    this.filterReports(this.filterForm.value);
  }

  clearUserFilters() {
    this.filterUsers = [];
    this.filterReports(this.filterForm.value);
  }


  async openReport(report, step?, key?, index?) {
    this.loaderService.show()
    this.reportSelected = true
    
    this.workflowService.getPublishedWorkflowVersion(report.workflowId, report.version)
      .then(workflowPublished => {
        this.loadedReport = report.id;
        const [instance, onClose] = this.detailPageService.loadComponent(ReportComponent, { report: report.id, step: step, key: key })
        instance.report = report
        instance.reportUser = this.getUsersName(report.userId)
        instance.workflowPublishedVersion = workflowPublished
        instance.step = step
        instance.key = key
        instance.index = index
        onClose.then(() => {
          this.reportSelected = false
          this.loadedReport = null;
        })
      })
      .catch(async error => {
        // Try to get all versions as fallback
        if (error.status === 404 && error.error === 'workflow-not-found') {
          try {
            const allVersions = await this.workflowService.getAllPublishedWorkflowVersions(report.workflowId);
            
            if (allVersions && allVersions.length > 0) {
              // Try to find the exact version
              const exactVersion = allVersions.find(v => v.version === report.version);
              if (exactVersion) {
                this.loadedReport = report.id;
                const [instance, onClose] = this.detailPageService.loadComponent(ReportComponent, { report: report.id, step: step, key: key })
                instance.report = report
                instance.reportUser = this.getUsersName(report.userId)
                instance.workflowPublishedVersion = exactVersion
                instance.step = step
                instance.key = key
                instance.index = index
                onClose.then(() => {
                  this.reportSelected = false
                  this.loadedReport = null;
                })
                this.loaderService.hide();
                return;
              }
            }
          } catch (fallbackError) {
            console.error('Failed to load workflow versions:', fallbackError);
          }
        }
        
        this.reportSelected = false;
        this.loadedReport = null;
        
        if (error.status === 404) {
          this.snackBarService.error('This workflow has been deleted or is no longer available.');
        } else {
          this.snackBarService.error('Error opening report. Please try again.');
        }
        
        this.loaderService.hide();
      })
  }

  importReports() {
    this.dbService.importReports()
      .then(errorCode => {
        if (errorCode) {
          if (errorCode === 'choose-canceled') {
            this.snackBarService.error(this.translateService.instant('MAIN.CONTENT.REPORT-LIST.IMPORT-CANCELED'))
          } else if (errorCode === 'reports-content-not-found') {
            this.snackBarService.error(this.translateService.instant('MAIN.CONTENT.REPORT.REPORT-NOT-FOUND'))
          } else if (errorCode === 'unknown-error') {
            this.snackBarService.error(this.translateService.instant('MODALS.GENERATE-WORKFLOW.INTERNAL-ERROR'))
          } else if (errorCode === 'wrong-report-format') {
            this.snackBarService.error(this.translateService.instant('MAIN.CONTENT.REPORT-LIST.REPORT-FORMAT-WRONG'))
          }
        } else {
          this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.REPORT-LIST.IMPORT-SUCCESSFUL'))
          this.getReportList();
        }
      })
  }

  changeToPrefilled(report) {
    const initialState= {
      textMessage: this.translateService.instant('MAIN.CONTENT.DASHBOARD.SUBMITTED-REPORT-TO-PREFILLED-TEXT'),
      confirmButtonText: this.translateService.instant('MAIN.CONTENT.DASHBOARD.CONTINUE'),
      confirmButtonStyle: 'primary',
      headerTitle: this.translateService.instant('MAIN.CONTENT.DASHBOARD.SUBMITTED-REPORT-TO-PREFILLED-TITLE'),
      instantClose:false
    };
    let confirmModalRef = this.modalService.show(AlertModalComponent,  {initialState, backdrop:'static', class:'modal-dialog-centered', animated:false});
    confirmModalRef.content.onClose.subscribe((res) => {
      if(res) {
        this.loaderService.show()
        this.workflowService.workflowReportToPrefilled(report.id).then(response => {
          report.status = 'prefilled'
          this.reportListAssign();
        })
        .catch(err => console.log(err))
        .finally(() => {})
      }
      confirmModalRef.hide();
    })
  }

  onTabClicked(index) {
    this.tabIndex = index
  }

  setDefaultDateFilter() {
    const currentTime = new Date().getTime();
    const oneWeekAgoTimestamp = 604800000; // equals to 7 days
    const oneWeekAgo = new Date(currentTime - oneWeekAgoTimestamp);
    const today = new Date(currentTime);
    
    // Set time to start of day for oneWeekAgo
    oneWeekAgo.setHours(0, 0, 0, 0);
    // Set time to end of day for today
    today.setHours(23, 59, 59, 999);
    
    this.filterStartEnd = [oneWeekAgo, today];
  }

}
